import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';

import { genericCompareFn } from 'src/app/utils/utils';
import {
  AGENT_HANDOFF_MODES,
  AGENT_HANDOFF_MODE_LABELS,
  AgentHandoffMode,
  INTAKE_MODES,
  INTAKE_MODE_LABELS,
  IntakeMode,
  IntakeSettings,
} from 'src/models/channels';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';

import { AutoresponderComponent } from '../autoresponder/autoresponder.component';
import { SupportStepsModule } from '../support-steps/support-steps.module';

export interface TicketIntakeConfig {
  intakeSettings: IntakeSettings;
  overrideIntakeSettings: boolean;
  intakeMode: IntakeMode;
  automatedTicketingEnabled: boolean | null;
  supportSteps?: any[];
  autoresponder?: any;
  supportStepRulesetId?: string;
}

export interface TicketIntakeStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  enabled: boolean;
  overridden: boolean;
  settings?: any;
  expandable: boolean;
  expanded?: boolean;
}

@Component({
  selector: 'app-ticket-intake-visualizer',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, SupportStepsModule, AutoresponderComponent, RouterModule],
  templateUrl: './ticket-intake-visualizer.component.html',
  styleUrls: ['./ticket-intake-visualizer.component.scss'],
})
export class TicketIntakeVisualizerComponent implements OnInit {
  constructor(
    private apiService: ApiService,
    private authService: AuthService,
  ) {}

  @Input() config: TicketIntakeConfig = {
    intakeSettings: {
      bot: {
        enabled: false,
        maxResponses: null,
        handoffMode: AGENT_HANDOFF_MODES.PROMPT,
      },
      form: {
        enabled: false,
      },
    },
    intakeMode: INTAKE_MODES.IMMEDIATE,
    overrideIntakeSettings: false,
    automatedTicketingEnabled: true,
    supportSteps: [],
    autoresponder: null,
  };

  @Input() editable: boolean = true;
  @Input() projectId: string | null = null;
  @Input() customerId: string | null = null;
  @Input() createdBy: string | null = null;
  @Input() showSlaDropdown: boolean = false;
  @Input() projectSupportSteps: any[] = [];
  @Input() customerSupportSteps: any[] = [];

  @Output() configChange = new EventEmitter<TicketIntakeConfig>();

  botMaxResponsesInput: string = '';
  botHandoffModeInput: AgentHandoffMode = AGENT_HANDOFF_MODES.PROMPT;
  supportStepRulesets: any[] = [];

  INTAKE_MODES = INTAKE_MODES;
  INTAKE_MODE_LABELS = INTAKE_MODE_LABELS;
  AGENT_HANDOFF_MODES = AGENT_HANDOFF_MODES;
  AGENT_HANDOFF_MODE_LABELS = AGENT_HANDOFF_MODE_LABELS;

  steps: TicketIntakeStep[] = [];

  ngOnInit() {
    this.initializeFromConfig();
    this.updateSteps();
  }

  private initializeFromConfig() {
    this.botMaxResponsesInput =
      this.config?.intakeSettings?.bot?.maxResponses != null
        ? this.config.intakeSettings.bot.maxResponses.toString()
        : '';
    this.botHandoffModeInput = this.config?.intakeSettings?.bot?.handoffMode || AGENT_HANDOFF_MODES.PROMPT;

    this.updateSupportStepsFromRuleset(this.config.supportStepRulesetId);
    this.loadSupportStepRulesets();
  }

  private updateSteps() {
    this.steps = [
      {
        id: 'intake',
        title: 'New Message Received',
        description: 'Handle any automated tracking of new messages in a channel',
        icon: 'chatbubble-outline',
        enabled: this.config.automatedTicketingEnabled,
        overridden: false,
        expandable: true,
        expanded: false,
      },
      {
        id: 'intake-processing',
        title: 'Ticket Intake Settings',
        description: 'Configure how messages get turned into tickets',
        icon: this.getIntakeIcon(),
        enabled: true,
        overridden: false,
        expandable: true,
        expanded: false,
      },
      {
        id: 'ticket-creation',
        title: 'Ticket Created',
        description: 'At this point, the support ticket is created. Any automations would kick off at this time.',
        icon: 'ticket-outline',
        enabled: true,
        overridden: false,
        expandable: false,
        expanded: false,
      },
      {
        id: 'auto-responder',
        title: 'Auto-Response',
        description: 'Send an automated response with AI or pre-set content',
        icon: 'arrow-undo-circle-outline',
        enabled: true,
        overridden: false,
        expandable: true,
        expanded: false,
      },
      {
        id: 'sla-assignment',
        title: 'SLA & Assignment',
        description: 'Assign tickets and set up automated escalations',
        icon: 'stopwatch-outline',
        enabled: true,
        overridden: false,
        expandable: true,
        expanded: false,
      },
    ];
  }

  private getIntakeIcon(): string {
    switch (this.config.intakeMode) {
      case INTAKE_MODES.IMMEDIATE:
        return 'flash-outline';
      case INTAKE_MODES.FORM:
        return 'document-outline';
      case INTAKE_MODES.BOT_TRIAGE:
        return 'custom-bot';
      default:
        return 'chatbubble-outline';
    }
  }

  onIntakeModeChange() {
    this.config = {
      ...this.config,
      intakeSettings: {
        ...this.config.intakeSettings,
        form: {
          enabled: this.config.intakeMode === INTAKE_MODES.FORM,
        },
        bot: {
          enabled: this.config.intakeMode === INTAKE_MODES.BOT_TRIAGE,
          maxResponses: this.botMaxResponsesInput ? parseInt(this.botMaxResponsesInput, 10) : null,
          handoffMode: this.botHandoffModeInput,
        },
      },
    };

    this.configChange.emit(this.config);
  }

  onBotSettingsChange() {
    this.config = {
      ...this.config,
      intakeSettings: {
        ...this.config.intakeSettings,
        bot: {
          ...this.config.intakeSettings.bot,
          maxResponses: this.botMaxResponsesInput ? parseInt(this.botMaxResponsesInput, 10) : null,
          handoffMode: this.botHandoffModeInput,
        },
      },
    };

    this.configChange.emit(this.config);
  }

  onStepClick(step: TicketIntakeStep) {
    // Toggle expanded state for expandable steps
    if (step.expandable) {
      step.expanded = !step.expanded;
    }
  }

  isSectionDisabled(step: TicketIntakeStep): boolean {
    return !step.overridden;
  }

  updateMaxResponses() {
    const value = String(this.botMaxResponsesInput).trim();
    if (value === '' || (!isNaN(Number(value)) && Number(value) > 0)) {
      this.onBotSettingsChange();
    } else {
      this.botMaxResponsesInput = '';
    }
  }

  getModeIcon(mode: IntakeMode): string {
    switch (mode) {
      case INTAKE_MODES.IMMEDIATE:
        return 'flash-outline';
      case INTAKE_MODES.FORM:
        return 'document-outline';
      case INTAKE_MODES.BOT_TRIAGE:
        return 'custom-bot';
      default:
        return 'chatbubble-outline';
    }
  }

  trackByStepId(index: number, step: TicketIntakeStep): string {
    return step.id;
  }

  isSupportStepsDisabled(): boolean {
    return this.config.supportStepRulesetId !== 'custom';
  }

  getStepSettingTitle(step: TicketIntakeStep): string {
    switch (step.id) {
      case 'intake':
        return 'Automatically trigger ticket intake';
      case 'intake-processing':
        return 'Intake processing mode';
      case 'auto-responder':
        return 'Send auto-response';
      case 'sla-assignment':
        return 'SLA & assignment rules';
      default:
        return 'Configure step';
    }
  }

  onSupportStepsChange(supportSteps: any[]) {
    this.config.supportSteps = supportSteps;

    this.configChange.emit(this.config);
  }

  onAutoresponderChange(autoresponder: any) {
    this.config.autoresponder = autoresponder;

    this.configChange.emit(this.config);
  }

  onAutomatedTicketingChange(event: any) {
    this.config = {
      ...this.config,
      automatedTicketingEnabled: event.detail.value,
    };
    this.configChange.emit(this.config);
  }

  automatedTicketingCompareFn = (o1: any, o2: any): boolean => {
    return o1 === o2;
  };

  automatedTicketingDefaultLabel(): string {
    const disableAutomatedTicketing = this.authService.tenant.disableAutomatedTicketing ?? false;

    if (disableAutomatedTicketing) {
      return 'Disabled';
    }

    return 'Enabled';
  }

  isAutomatedTicketingEnabled(): boolean {
    return (
      this.config.automatedTicketingEnabled ||
      (this.config.automatedTicketingEnabled === null && !this.authService.tenant.disableAutomatedTicketing)
    );
  }

  onSlaRulesetChange(event: any) {
    this.config.supportStepRulesetId = event.detail.value;

    this.updateSupportStepsFromRuleset(this.config.supportStepRulesetId);

    this.configChange.emit(this.config);
  }

  private updateSupportStepsFromRuleset(rulesetId: string) {
    if (!rulesetId) {
      return;
    }

    let newSupportSteps: any[] = [];

    if (rulesetId === 'default') {
      // Use project default support steps
      newSupportSteps = this.projectSupportSteps || [];
    } else if (rulesetId === 'custom') {
      // Use customer-specific support steps
      newSupportSteps = this.customerSupportSteps || [];
    } else {
      // Find the selected ruleset and update support steps
      const selectedRuleset = this.supportStepRulesets.find((ruleset) => ruleset.id === rulesetId);
      if (selectedRuleset) {
        newSupportSteps = selectedRuleset.rules || [];
      }
    }

    // Create a new array reference to ensure change detection
    this.config = {
      ...this.config,
      supportSteps: [...newSupportSteps],
    };

    // Emit the config change to notify parent components
    this.configChange.emit(this.config);
  }

  async loadSupportStepRulesets() {
    const supportStepRulesets: any = await this.apiService.postPromise(`/support-step-rulesets/list`, {});

    this.supportStepRulesets = supportStepRulesets.data;
  }

  compareFn(v1: any, v2: any): boolean {
    return genericCompareFn(v1, v2);
  }

  onOverrideIntakeSettingsChange() {
    if (!this.config.overrideIntakeSettings) {
      // Set intakeMode to tenant-level default values
      const defaultIntakeSettings = this.authService.tenant?.defaultChannelIntakeSettings;
      this.config.intakeMode = defaultIntakeSettings?.form?.enabled
        ? INTAKE_MODES.FORM
        : defaultIntakeSettings?.bot?.enabled
          ? INTAKE_MODES.BOT_TRIAGE
          : INTAKE_MODES.IMMEDIATE;
    }

    this.configChange.emit(this.config);
  }
}
